<template>
  <v-app>
    <v-app-bar app>
      <v-toolbar-title>{{ t('admConfig.title') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="goBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
    </v-app-bar>

    <v-main>
      <v-container fluid>
        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title>
                <v-icon left>mdi-cog</v-icon>
                {{ t('admConfig.configuration') }}
              </v-card-title>
              <v-card-text>
                <p>{{ t('admConfig.description') }}</p>

                <!-- Qui aggiungeremo i controlli di configurazione -->
                <v-row>
                  <v-col cols="12">
                    <v-card outlined>
                      <v-card-title>{{ t('admConfig.generalSettings') }}</v-card-title>
                      <v-card-text>
                        <!-- Root unica del contenitore: le sottocartelle sono fisse (config backend) -->
                        <v-text-field
                          :label="t('admConfig.rootPath')"
                          v-model="config.rootPath"
                          outlined
                          :placeholder="t('admConfig.rootPathPlaceholder')"
                          :hint="t('admConfig.rootPathHint')"
                          persistent-hint
                        />

                        <!-- Anteprima struttura risultante (sola lettura) -->
                        <v-sheet v-if="config.rootPath && folders.processes" color="grey-lighten-4" rounded class="pa-3 mt-4">
                          <div class="text-caption text-medium-emphasis mb-1">{{ t('admConfig.structurePreview') }}</div>
                          <pre class="text-body-2 mb-0" style="white-space: pre-wrap;">{{ structurePreview }}</pre>
                        </v-sheet>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <v-row class="mt-4">
                  <v-col cols="12">
                    <v-card outlined>
                      <v-card-title>{{ t('admConfig.systemInfo') }}</v-card-title>
                      <v-card-text>
                        <v-simple-table>
                          <tbody>
                            <tr>
                              <td><strong>{{ t('admConfig.status') }}</strong></td>
                              <td>
                                <v-chip
                                  :color="systemStatus.color"
                                  small
                                >
                                  {{ t(`admConfig.${systemStatus.status}`) }}
                                </v-chip>
                              </td>
                            </tr>
                            <tr>
                              <td><strong>{{ t('admConfig.lastUpdate') }}</strong></td>
                              <td>{{ systemStatus.lastUpdate }}</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="primary"
                  @click="saveConfiguration"
                  :loading="saving"
                  :disabled="!isConfigValid"
                >
                  {{ t('admConfig.save') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <!-- Editor definizione campi (Classes/fields/<id>.json) — solo superadmin -->
        <v-row v-if="isSuperAdmin">
          <v-col cols="12">
            <FieldsEditor />
          </v-col>
        </v-row>

        <!-- Editor colonne della dashboard (columns.json) — solo superadmin -->
        <v-row v-if="isSuperAdmin">
          <v-col cols="12">
            <ColumnsEditor />
          </v-col>
        </v-row>

        <!-- Editor stati e workflow (cfg.json + instructions.json) — solo superadmin -->
        <v-row v-if="isSuperAdmin">
          <v-col cols="12">
            <StatusEditor />
          </v-col>
        </v-row>

        <!-- Profili operativi e visibilità fuori dalle proprie fasi (cfg.json) — solo superadmin.
             I profili creati qui si assegnano alle fasi nell'editor stati, qui sopra. -->
        <v-row v-if="isSuperAdmin">
          <v-col cols="12">
            <RolesEditor />
          </v-col>
        </v-row>

        <!-- Tool esterni (Classes/tools.json): quale campo leggere, dove scrivere l'esito — solo superadmin.
             Le azioni engine "tool" della biblioteca puntano a queste chiamate. -->
        <v-row v-if="isSuperAdmin">
          <v-col cols="12">
            <ToolsEditor />
          </v-col>
        </v-row>

        <!-- Manutenzione: allineamento retroattivo dei checkbox di validazione — solo superadmin.
             Serve dopo aver aggiunto nuovi checkbox al workflow: i processi già avanti restano
             altrimenti bloccati da validazioni di fasi che hanno superato prima che esistessero. -->
        <v-row v-if="isSuperAdmin">
          <v-col cols="12">
            <v-card outlined>
              <v-card-title>
                <v-icon left>mdi-checkbox-multiple-marked-outline</v-icon>
                {{ t('admConfig.backfill.title') }}
              </v-card-title>
              <v-card-text>
                <p class="text-body-2 text-medium-emphasis">{{ t('admConfig.backfill.description') }}</p>

                <v-alert v-if="backfill.error" type="error" density="compact" class="mb-3">
                  {{ backfill.error }}
                </v-alert>

                <div v-if="backfill.result">
                  <v-alert
                    :type="backfill.result.projects ? 'info' : 'success'"
                    density="compact"
                    class="mb-3"
                  >
                    <div>
                      {{ backfill.result.projects
                        ? t('admConfig.backfill.summary', {
                            fields: backfill.result.fields,
                            projects: backfill.result.projects,
                            scanned: backfill.result.scanned
                          })
                        : t('admConfig.backfill.none') }}
                    </div>
                    <div class="text-caption">
                      {{ backfill.result.dryRun
                        ? t('admConfig.backfill.dryRunNote')
                        : t('admConfig.backfill.doneNote') }}
                    </div>
                  </v-alert>

                  <v-table v-if="backfill.result.changes.length" density="compact">
                    <thead>
                      <tr>
                        <th>{{ t('admConfig.backfill.project') }}</th>
                        <th>{{ t('admConfig.backfill.status') }}</th>
                        <th>{{ t('admConfig.backfill.fields') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="change in backfill.result.changes" :key="change.idProject">
                        <td>
                          {{ change.idProject }}
                          <span v-if="change.isArchived" class="text-caption text-medium-emphasis">
                            ({{ t('admConfig.backfill.archived') }})
                          </span>
                        </td>
                        <td>{{ change.activeStatus }}</td>
                        <td>{{ change.labels.join(', ') }}</td>
                      </tr>
                    </tbody>
                  </v-table>
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn
                  variant="text"
                  :loading="backfill.loading"
                  @click="runBackfill(true)"
                >
                  {{ t('admConfig.backfill.simulate') }}
                </v-btn>
                <!-- L'applicazione si sblocca solo dopo una simulazione con qualcosa da fare:
                     la scrittura di massa non parte mai al primo clic. -->
                <v-btn
                  color="primary"
                  :loading="backfill.loading"
                  :disabled="!canApplyBackfill"
                  @click="backfill.confirming = true"
                >
                  {{ t('admConfig.backfill.apply') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <ConfirmDialog
          v-model="backfill.confirming"
          :title="t('admConfig.backfill.title')"
          :message="backfillConfirmMessage"
          :confirm-text="t('admConfig.backfill.apply')"
          :cancel-text="t('common.cancel')"
          :loading="backfill.loading"
          @confirm="runBackfill(false)"
        />

        <!-- Azioni IA, skill, motore, credito e consumi stanno nella pagina Gestione IA (AiAdmin.vue) -->
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useLocalBackendStore } from '../stores/localBackend.js'
import { useProjectStore } from '../stores/projects.js'
import { useAuthStore } from '../stores/auth.js'
import { apiCall } from '../utils/api.js'
import FieldsEditor from '../components/FieldsEditor.vue'
import ColumnsEditor from '../components/ColumnsEditor.vue'
import StatusEditor from '../components/StatusEditor.vue'
import RolesEditor from '../components/RolesEditor.vue'
import ToolsEditor from '../components/ToolsEditor.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'

const { t } = useI18n()
const router = useRouter()
const localBackendStore = useLocalBackendStore()
const projectStore = useProjectStore()
const authStore = useAuthStore()

const isSuperAdmin = computed(() => authStore.user?.role === 'superadmin')

// Stato reattivo per la configurazione (solo la root del contenitore)
const config = reactive({
  rootPath: ''
})

// Stato del sistema
const systemStatus = reactive({
  status: 'online',
  color: 'success',
  lastUpdate: new Date().toLocaleString()
})

const saving = ref(false)

// Config strutturale cartelle (dal Backend) per l'anteprima
const folders = computed(() => projectStore.folders || {})

const isConfigValid = computed(() => true)

// Anteprima della struttura risultante: root + cartelle della sezione "Folders" di appsettings.json,
// nello stesso ordine del file. Ogni riga compare solo se la sua chiave è configurata, così
// l'anteprima non mostra mai una cartella che il Backend non conosce.
const structurePreview = computed(() => {
  const root = (config.rootPath || '').replace(/[\\/]+$/, '')
  const f = folders.value
  if (!root || !f.processes) return ''

  const allTypes = t('admConfig.allTypesNote')
  const lines = [`${root}\\`, `  ${f.processes}/   (${allTypes})`]

  if (f.templates) {
    lines.push(`  ${f.templates}/`)
    Object.values(f.templatesByType || {}).forEach(name => lines.push(`    ${name}/`))
  }

  if (f.archive) lines.push(`  ${f.archive}/   (${allTypes})`)

  // Documenti condivisi: fonti standard lette dalle verifiche IA di qualsiasi processo
  if (f.documents) lines.push(`  ${f.documents}/   (${t('admConfig.sharedDocumentsNote')})`)

  return lines.join('\n')
})

// ─── Allineamento retroattivo dei checkbox di validazione (solo superadmin) ───
// Prima si simula (dryRun), poi si applica: il pulsante "Applica" resta spento finché una
// simulazione non ha detto che c'è davvero qualcosa da spuntare.
const backfill = reactive({
  loading: false,
  error: '',
  result: null,
  confirming: false
})

const canApplyBackfill = computed(() =>
  !!backfill.result && backfill.result.dryRun && backfill.result.projects > 0)

const backfillConfirmMessage = computed(() => t('admConfig.backfill.applyConfirm', {
  fields: backfill.result?.fields ?? 0,
  projects: backfill.result?.projects ?? 0
}))

const runBackfill = async (dryRun) => {
  backfill.loading = true
  backfill.error = ''
  try {
    backfill.result = await apiCall(
      `/api/projects/maintenance/backfill-validations?dryRun=${dryRun}`,
      { method: 'POST' }
    )
    backfill.confirming = false
  } catch (error) {
    console.error('Errore nell\'allineamento delle validazioni:', error)
    backfill.error = error?.message || t('admConfig.backfill.error')
  } finally {
    backfill.loading = false
  }
}

// Metodi
const goBack = () => {
  router.push('/home')
}

const saveConfiguration = async () => {
  saving.value = true
  try {
    // Salva la root nel LocalBackend
    const result = await apiCall('/api/config/config', {
      method: 'PUT',
      body: JSON.stringify({
        rootPath: config.rootPath
      }),
      backend: 'local',
      includeAuth: false
    })

    console.log('Configurazione salvata con successo', result)

    // Riverifica la configurazione dopo il salvataggio
    await localBackendStore.checkConnection()

    // Torna alla dashboard dopo il salvataggio
    router.push('/home')
  } catch (error) {
    console.error('Errore nel salvataggio:', error)
  } finally {
    saving.value = false
  }
}

const loadConfiguration = async () => {
  try {
    // Carica la root dal LocalBackend
    const pathsConfig = await apiCall('/api/config/config', {
      method: 'GET',
      backend: 'local',
      includeAuth: false
    })

    if (pathsConfig) {
      config.rootPath = pathsConfig.rootPath || ''
    }
  } catch (error) {
    console.error('Errore nel caricamento:', error)
  }
}

onMounted(async () => {
  // Assicura che la config strutturale (nomi cartelle) sia disponibile per l'anteprima
  if (!folders.value.processes) {
    await projectStore.loadConfigurations()
  }
  loadConfiguration()
})
</script>

<style scoped>
.v-card {
  margin-bottom: 16px;
}
</style>
